import type {
  EventCreationForm,
  TicketTypeFormType,
} from "@punchup/venues/client/components/tixologi-wizard/types";
import dayjs, { Dayjs } from "@punchup/utils/dayjs";
import { toWirePercentageFee } from "../percentage-fee";

type TicketTypeForm = TicketTypeFormType[number];

const SALE_WINDOW_CUSTOM = "custom";
const OFF_SALE_AT_START = "start_date";
const OFF_SALE_AT_END = "end_date";

function wallClockInZone(date: string, time: string, timeZone: string): Dayjs {
  const [hour, minute] = time.split(":").map((part) => parseInt(part, 10));
  return dayjs.tz(date, timeZone).hour(hour).minute(minute);
}

function resolveSellingDate(
  ticketType: TicketTypeForm,
  start: Dayjs,
  now: Dayjs
): string {
  if (ticketType.selling_date === SALE_WINDOW_CUSTOM) {
    if (!ticketType.custom_selling_datetime) {
      throw new Error(
        "Custom selling datetime is required when selling_date is custom"
      );
    }
    return ticketType.custom_selling_datetime;
  }
  const hoursFromStart = parseFloat(ticketType.selling_date);
  if (hoursFromStart === 0) {
    return now.hour(0).minute(0).second(0).millisecond(0).toISOString();
  }
  return start.add(hoursFromStart, "hours").toISOString();
}

function resolveOffSellingDate(
  ticketType: TicketTypeForm,
  start: Dayjs,
  end: Dayjs
): string | null {
  if (ticketType.off_selling_date === SALE_WINDOW_CUSTOM) {
    if (!ticketType.custom_off_selling_datetime) {
      throw new Error(
        "Custom off selling datetime is required when off_selling_date is custom"
      );
    }
    return ticketType.custom_off_selling_datetime;
  }
  if (ticketType.off_selling_date === OFF_SALE_AT_START) {
    return start.toISOString();
  }
  if (ticketType.off_selling_date === OFF_SALE_AT_END) {
    return end.toISOString();
  }
  if (ticketType.off_selling_date) {
    return dayjs(start)
      .add(parseFloat(ticketType.off_selling_date), "hours")
      .toISOString();
  }
  return null;
}

function buildTicketType(
  ticketType: TicketTypeForm,
  start: Dayjs,
  end: Dayjs,
  now: Dayjs
) {
  return {
    ...ticketType,
    selling_date: resolveSellingDate(ticketType, start, now),
    off_selling_date: resolveOffSellingDate(ticketType, start, end),
    fixed_fee: ticketType.fixed_fee || 0,
    percentage_fee: toWirePercentageFee(ticketType.percentage_fee ?? 0),
    final_price: ticketType.final_price || ticketType.initial_price,
    tag_ids: (ticketType.tags || []).map((tag) => tag.id),
  };
}

function buildRecurringDates(
  dates: EventCreationForm["showDetails"]["dates"],
  timeZone: string
) {
  return dates?.map((date) => {
    if (!date) return;
    return {
      start_date: dayjs(date.start_date).tz(timeZone, true).utc().toISOString(),
      end_date: dayjs(date.end_date).tz(timeZone, true).utc().toISOString(),
    };
  });
}

/**
 * Shapes a completed wizard form into the body `POST /punchup/events` expects.
 * Pure: the only clock it reads is `now`, which decides the "on sale now"
 * selling date (start of that day).
 */
export function buildTixologiEventPayload(
  event: EventCreationForm,
  now: Dayjs = dayjs()
) {
  const { showDetails } = event;
  const { start_date, start_time, end_date, end_time, time_zone } = showDetails;

  const start = wallClockInZone(start_date, start_time, time_zone);
  const end = wallClockInZone(end_date, end_time, time_zone);

  const gateTime = showDetails.doors_open
    ? parseFloat(showDetails.doors_open)
    : null;
  const doorTime =
    gateTime !== null ? start.add(gateTime, "hours").toISOString() : null;

  const hiddenUntil = showDetails.hidden_until
    ? dayjs(showDetails.hidden_until).tz(time_zone).startOf("day").toISOString()
    : null;

  return {
    ...event,
    ticketTypes: event.ticketTypes.map((ticketType) =>
      buildTicketType(ticketType, start, end, now)
    ),
    showDetails: {
      ...showDetails,
      gate_time: gateTime,
      door_time: doorTime,
      image_url: showDetails.image_url || null,
      banner_image_url: showDetails.banner_image_url || null,
      banner_link_url: showDetails.banner_link_url || null,
      ticket_delivery_hours_before_event:
        showDetails.ticket_delivery_hours_before_event ?? null,
      start_date: start.toISOString(),
      end_date: end.toISOString(),
      dates: buildRecurringDates(showDetails.dates, time_zone),
      seats_chart_key: event.seatCharts?.selectedChartKey || null,
      tag_ids: (showDetails.tags || []).map((tag) => tag.id),
      hidden_until: hiddenUntil,
      // Admin-only: create the event hidden from every public surface and unsellable
      // (see ticketing-api Event.inactive). Explicit so the wire contract is visible.
      inactive: showDetails.inactive ?? false,
    },
  };
}

export type TixologiCreateEventBody = ReturnType<
  typeof buildTixologiEventPayload
>;
